import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, MessageCircle, Shield, Users, Zap, Send } from 'lucide-react';
import SEO from '../components/SEO';
import RegistrationModal from '../components/RegistrationModal';
import DialCodeSelect from '../components/DialCodeSelect';
import { getDetectedCountry, getCountryList, submitLead } from '../services/api';

const ContactPage = () => {
  const countries = getCountryList();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', countryDialCode: 1, country: 'US', message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [geoData, setGeoData] = useState(null);
  const [rawGeo, setRawGeo] = useState(null);

  useEffect(() => {
    (async () => {
      const detected = await getDetectedCountry();
      setGeoData(detected.geoData);
      setRawGeo(detected.rawGeo);
      setFormData(prev => ({ ...prev, country: detected.countryCode, countryDialCode: detected.dialCode }));
    })();
  }, []);

  const validateEmail = (email) => {
    const t = email.trim();
    if (!t || (t.match(/@/g) || []).length !== 1) return false;
    const [local, domain] = t.split('@');
    if (!local || !domain || !domain.includes('.')) return false;
    return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(t);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') setFormData(prev => ({ ...prev, phone: value.replace(/\D/g, '').substring(0, 10) }));
    else setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Phone must be exactly 10 digits';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setApiError('');
    setIsSubmitting(true);
    const result = await submitLead({
      name: formData.name, email: formData.email, phoneNumber: formData.phone,
      countryDialCode: formData.countryDialCode, country: formData.country,
      description: formData.message, geoData, rawGeo,
    });
    setIsSubmitting(false);
    if (result.success) window.location.href = result.redirectUrl;
    else setApiError(result.error);
  };

  return (
    <>
      <SEO
        title="Contact Us | Norfolk Trademark - Get Your Free Consultation"
        description="Contact Norfolk Trademark for expert trademark registration assistance."
        keywords="contact trademark attorney, trademark consultation"
        canonical="/contact"
      />
      <div className="min-h-screen">
        {/* Hero */}
        <div className="relative text-white py-16 overflow-hidden">
          <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
            <div className="inline-block bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-white/20">Free Consultation</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Get In Touch</h1>
            <p className="text-base text-white/80 max-w-2xl mx-auto">Have questions about trademark registration? Our experts are ready to help you protect your brand.</p>
          </div>
        </div>

        {/* Main: Form + Contact Info */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

              {/* Left — Form (3 cols) */}
              <div className="lg:col-span-3">
                <h2 className="text-3xl md:text-4xl font-bold text-black mb-2">Send Us a Message</h2>
                <p className="text-base text-gray-600 mb-6">Fill out the form and we'll respond within 24 hours.</p>

                {apiError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{apiError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} className={`w-full px-4 py-3 text-sm border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent`} placeholder="John Doe" data-testid="contact-name" />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address *</label>
                      <input type="text" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 text-sm border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent`} placeholder="john@example.com" data-testid="contact-email" />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number *</label>
                    <div className="flex gap-2">
                      <DialCodeSelect
                        value={formData.countryDialCode}
                        onChange={(dialCode) => { const m = countries.find(c => c.dialCode === dialCode); setFormData(prev => ({ ...prev, countryDialCode: dialCode, country: m ? m.code : prev.country })); }}
                        countries={countries}
                        testId="contact-dialcode"
                      />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} onKeyDown={(e) => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault(); }} maxLength="10" inputMode="numeric" className={`flex-1 px-4 py-3 text-sm border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent`} placeholder="1234567890" data-testid="contact-phone" />
                    </div>
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Your Message *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows="4" className={`w-full px-4 py-3 text-sm border ${errors.message ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent`} placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" data-testid="contact-message" />
                    {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                  </div>

                  <button type="submit" disabled={isSubmitting} className="bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-bold px-8 py-3 text-sm rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2 disabled:opacity-50" data-testid="contact-submit">
                    {isSubmitting ? 'Sending...' : <><Send className="w-4 h-4" /> Send Message</>}
                  </button>
                </form>
              </div>

              {/* Right — Contact Info (2 cols) */}
              <div className="lg:col-span-2 space-y-5">
                <h3 className="text-lg font-bold text-black mb-4">Contact Information</h3>

                <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <div className="w-11 h-11 bg-[#FF035B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#FF035B]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-black">Email</p>
                    <a href="mailto:info@norfolktrademark.com" className="text-sm text-gray-600 hover:text-[#FF035B]">info@norfolktrademark.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <div className="w-11 h-11 bg-[#FF035B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#FF035B]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-black">Phone</p>
                    <a href="tel:+10000000000" className="text-sm text-gray-600 hover:text-[#FF035B]">+1 (000) 000-0000</a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <div className="w-11 h-11 bg-[#FF035B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#FF035B]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-black">Office Address</p>
                    <p className="text-sm text-gray-600">515 Flower Street 18th and 19th Floors, Los Angeles, CA 90071</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <div className="w-11 h-11 bg-[#FF035B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#FF035B]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-black">Business Hours</p>
                    <p className="text-sm text-gray-600">Mon - Fri: 9AM - 6PM EST</p>
                    <p className="text-sm text-gray-600">Sat - Sun: Closed</p>
                  </div>
                </div>

                {/* Live Chat Button */}
                <button
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#2F1A8E] to-[#FF035B] text-white font-semibold px-6 py-3 rounded-xl hover:from-[#FF035B] hover:to-[#2F1A8E] transition-all duration-300 text-sm"
                  data-testid="contact-chat-btn"
                >
                  <MessageCircle className="w-4 h-4" /> Start Live Chat
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-2">Find Us</h2>
              <p className="text-base text-gray-600">Visit our office in Los Angeles, California</p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200">
              <iframe
                title="Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3305.8!2d-118.2558!3d34.0522!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2c7b5a1b3c0f1%3A0x1234567890abcdef!2s515%20Flower%20St%2C%20Los%20Angeles%2C%20CA%2090071!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              />
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Why Clients Choose Us</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-black mb-1">24-Hour Response</h3>
                  <p className="text-sm text-gray-600">We respond to all inquiries within one business day guaranteed.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-black mb-1">Licensed Attorneys</h3>
                  <p className="text-sm text-gray-600">All consultations handled by USPTO-registered trademark attorneys.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-black mb-1">Trusted Service</h3>
                  <p className="text-sm text-gray-600">Thousands of businesses trust us for trademark protection.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <RegistrationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} initialType="registration" />
    </>
  );
};

export default ContactPage;
